import type { EditRow } from "@/common/lib/api/documentEdits";
import { listLines, wordParts, type DiffKind } from "./editDiff";

const toText = (v: string | string[] | null | undefined) => (Array.isArray(v) ? v.join(" ") : (v ?? ""));
const toLines = (v: string | string[] | null | undefined) => (Array.isArray(v) ? v : v ? [v] : []);

const PART_CLASS: Record<DiffKind, string> = {
    same: "",
    del: "bg-blaze-25 text-blaze-800 line-through rounded-[3px] px-px",
    ins: "bg-[#ecfdf3] text-green-700 no-underline rounded-[3px] px-px",
};
const LINE_CLASS: Record<DiffKind, string> = {
    same: "text-gray-900",
    del: "bg-blaze-25 text-blaze-800 line-through",
    ins: "bg-[#ecfdf3] text-green-700",
};
const MARK: Record<DiffKind, string> = { same: "•", del: "−", ins: "+" };

const Part = ({ kind, text }: { kind: DiffKind; text: string }) => {
    if (kind === "del") return <del className={PART_CLASS.del}>{text}</del>;
    if (kind === "ins") return <ins className={PART_CLASS.ins}>{text}</ins>;
    return <span>{text}</span>;
};

/** EN-1492: the shared diff body for a row, used by the modal and the single-field confirm. */
export const EditRowDiff = ({ row }: { row: EditRow }) => {
    const isList = row.kind === "list" || Array.isArray(row.baseline) || Array.isArray(row.current);
    if (isList && row.kind !== "title") {
        const lines =
            row.kind === "extra"
                ? toLines(row.current).map((text) => ({ text, kind: "ins" as DiffKind }))
                : row.tag === "cleared"
                  ? toLines(row.baseline).map((text) => ({ text, kind: "del" as DiffKind }))
                  : listLines(toLines(row.baseline), toLines(row.current));
        return (
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-sm leading-[22px]">
                {lines.map((l, i) => (
                    <li key={i} className={`flex gap-2 rounded px-1.5 ${LINE_CLASS[l.kind]}`}>
                        <span aria-hidden="true" className="w-3 flex-none font-bold no-underline">
                            {MARK[l.kind]}
                        </span>
                        <span className="min-w-0 whitespace-pre-wrap break-words">{l.text}</span>
                    </li>
                ))}
            </ul>
        );
    }
    const parts =
        row.kind === "extra"
            ? [{ text: toText(row.current), kind: "ins" as DiffKind }]
            : row.tag === "cleared"
              ? [{ text: toText(row.baseline), kind: "del" as DiffKind }]
              : wordParts(toText(row.baseline), toText(row.current));
    return (
        // edited text keeps its line breaks (bullets, markdown), and a long unbroken string wraps
        <div className="min-w-0 whitespace-pre-wrap break-words text-sm leading-[22px] text-gray-900">
            {parts.map((p, i) => (
                <Part key={i} kind={p.kind} text={p.text} />
            ))}
        </div>
    );
};

/** Section name above the row name, shown unless the row is the section itself. */
export const rowSectionLabel = (row: EditRow) => (row.sectionName && row.id !== row.sectionId ? row.sectionName : null);
